import { boxPlanningKeys } from "@/modules/boxPlanning/gateway/boxPlanningKeys"
import { boxSlotKeys } from "@/modules/boxSlot/gateway/boxSlotKeys"
import { BoxSlotTemplateEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateEntity"
import { BoxSlotTemplateWeekEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateWeekEntity"
import type { SlotTemplateRequestDto } from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateDto"
import { boxSlotTemplateKeys } from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateKeys"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useBoxSlotTemplate = (boxProgramId: string, boxSlotTemplateId: string) => {
  const { boxSlotTemplateApi } = useServicesContext()

  return useSuspenseQuery({
    queryKey: boxSlotTemplateKeys.slotTemplate(boxProgramId, boxSlotTemplateId),
    queryFn: async () => {
      const result = await boxSlotTemplateApi.getBoxSlotTemplate(boxProgramId, boxSlotTemplateId)
      return BoxSlotTemplateEntity.fromDto(result)
    },
  })
}

export const useBoxSlotTemplates = (boxProgramId: string) => {
  const { boxSlotTemplateApi } = useServicesContext()

  return useSuspenseQuery({
    queryKey: boxSlotTemplateKeys.slotTemplates(boxProgramId),
    queryFn: async () => {
      const result = await boxSlotTemplateApi.getBoxSlotsTemplates(boxProgramId)
      return BoxSlotTemplateWeekEntity.fromDto(result)
    },
  })
}

export const useAllBoxSlotTemplates = () => {
  const { boxSlotTemplateApi } = useServicesContext()
  return useSuspenseQuery({
    queryKey: boxSlotTemplateKeys.all,
    queryFn: async () => {
      const result = await boxSlotTemplateApi.getAllBoxSlotsTemplates()
      return BoxSlotTemplateWeekEntity.fromDto(result)
    },
  })
}

export const useBoxSlotTemplateCreate = () => {
  const { boxSlotTemplateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      boxProgramId,
      boxSlotTemplate,
    }: {
      boxProgramId: string
      boxSlotTemplate: SlotTemplateRequestDto
    }) => {
      await boxSlotTemplateApi.createBoxSlotTemplate(boxProgramId, boxSlotTemplate)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boxSlotTemplateKeys.all })
      void queryClient.invalidateQueries({ queryKey: boxPlanningKeys.all })
      void queryClient.invalidateQueries({ queryKey: boxSlotKeys.all })
    },
  })
}

export const useBoxSlotTemplateCreateList = () => {
  const { boxSlotTemplateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      boxProgramId,
      boxSlotTemplates,
    }: {
      boxProgramId: string
      boxSlotTemplates: SlotTemplateRequestDto[]
    }) => {
      await boxSlotTemplateApi.createBoxSlotsTemplateList(boxProgramId, boxSlotTemplates)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boxSlotTemplateKeys.all })
      void queryClient.invalidateQueries({ queryKey: boxPlanningKeys.all })
      void queryClient.invalidateQueries({ queryKey: boxSlotKeys.all })
    },
  })
}

export const useBoxSlotTemplateUpdate = () => {
  const { boxSlotTemplateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      boxProgramId,
      boxSlotTemplateId,
      boxSlotTemplate,
    }: {
      boxProgramId: string
      boxSlotTemplateId: string
      boxSlotTemplate: SlotTemplateRequestDto
    }) => {
      return await boxSlotTemplateApi.updateBoxSlotTemplate(boxProgramId, boxSlotTemplateId, boxSlotTemplate)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boxSlotTemplateKeys.all })
      void queryClient.invalidateQueries({ queryKey: boxPlanningKeys.all })
      void queryClient.invalidateQueries({ queryKey: boxSlotKeys.all })
    },
  })
}

export const useBoxSlotTemplateDelete = () => {
  const { boxSlotTemplateApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      boxProgramId,
      slotTemplateId,
      impactAlreadyCreatedSlots,
    }: {
      boxProgramId: string
      slotTemplateId: string
      impactAlreadyCreatedSlots: boolean
    }) => {
      await boxSlotTemplateApi.deleteBoxSlotTemplate(boxProgramId, slotTemplateId, impactAlreadyCreatedSlots)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boxSlotTemplateKeys.all })
      void queryClient.invalidateQueries({ queryKey: boxPlanningKeys.all })
      void queryClient.invalidateQueries({ queryKey: boxSlotKeys.all })
    },
  })
}
